import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { cn, HelpHint, SortableHeader, Table, TableColumnsMenu, tableColumnsStorageKey, } from '@wit-support/ui';
/**
 * Ключ хранения настроек колонок таблицы раздела:
 * `ui.table.fb-launches.<tableId>.v1`.
 */
export function adsTableColumnsKey(tableId) {
    return tableColumnsStorageKey('fb-launches', tableId);
}
/** id колонки меню действий строки: подпись в шапке только для скринридера. */
export const ACTIONS_COLUMN = 'actions';
/** Описание колонки действий: не скрывается, не переставляется, закреплена справа. */
export const ACTIONS_COLUMN_DEF = {
    id: ACTIONS_COLUMN,
    label: 'Действия',
    hideable: false,
    reorderable: false,
    // НЕ `pinnable: false`: в ките это «не закрепляется» и обнуляет `defaultPin`, колонка
    // осталась бы без закрепления справа (см. `columns-state.ts`, `defaultState`).
    defaultPin: 'right',
};
/** Составной ключ «(группа, значение)» для `sortRows`. */
export function groupedSortValue(group, value) {
    return { group, value };
}
function isSortGroup(v) {
    return typeof v === 'object' && v !== null;
}
const collator = new Intl.Collator('ru', { numeric: true, sensitivity: 'base' });
function compareValues(a, b) {
    if (typeof a === 'number' && typeof b === 'number')
        return a - b;
    return collator.compare(String(a), String(b));
}
const isEmpty = (v) => isSortGroup(v)
    ? isEmpty(v.value)
    : v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v));
/**
 * Клиентская сортировка строк таблицы, у которой ВСЕ строки на странице (без
 * пагинации): по-честному, а не «сортировка одной страницы из двадцати».
 * Стабильная; пустые значения — в конце при любом направлении. `sort: null`
 * — порядок сервера.
 */
export function sortRows(rows, sort, valueOf) {
    if (!sort)
        return [...rows];
    const sign = sort.dir === 'asc' ? 1 : -1;
    return rows
        .map((row, i) => ({ row, i, v: valueOf(row, sort.key) }))
        .sort((x, y) => {
        const ex = isEmpty(x.v);
        const ey = isEmpty(y.v);
        if (ex || ey)
            return ex === ey ? x.i - y.i : ex ? 1 : -1;
        if (isSortGroup(x.v) && isSortGroup(y.v)) {
            const gx = x.v.group || null;
            const gy = y.v.group || null;
            if (gx !== gy)
                return gx === null ? 1 : gy === null ? -1 : collator.compare(gx, gy);
            return sign * compareValues(x.v.value, y.v.value) || x.i - y.i;
        }
        return sign * compareValues(x.v, y.v) || x.i - y.i;
    })
        .map((x) => x.row);
}
/**
 * Состояние сортировки таблицы с колонками из `useTableColumns`: если колонку,
 * по которой сортируют, убрали из видимых, сортировка сбрасывается в `null`
 * (порядок сервера) — иначе таблица остаётся упорядоченной по невидимому.
 * Вернули колонку — сортировка уже не возвращается сама.
 */
export function useColumnSort(columns) {
    const [stored, setSort] = useState(null);
    const alive = stored !== null && columns.visibleIds.includes(stored.key);
    useEffect(() => {
        if (stored !== null && !alive)
            setSort(null);
    }, [stored, alive]);
    return [alive ? stored : null, setSort];
}
/**
 * Заголовок колонки из `useTableColumns`: сортируемый (`SortableHeader`) или
 * простой, с ⓘ-пояснением при надобности. Закрепление — через `column`.
 */
export function ColumnHeader({ column, sort, onSortChange, sortable = true, defaultDirection = 'asc', align = 'left', hint, className, children, }) {
    const label = children ?? column.def.label;
    if (sortable && sort !== undefined && onSortChange) {
        return (_jsx(SortableHeader, { column: column, sortKey: column.id, sort: sort, onSortChange: onSortChange, defaultDirection: defaultDirection, align: align, className: className, ...(hint ? { hint } : {}), children: label }));
    }
    return (_jsx(Table.HeaderCell, { column: column, className: cn(align === 'right' && 'text-right', className), children: hint ? (_jsxs("span", { className: cn('inline-flex items-center gap-0.5 align-middle', align === 'right' && 'flex-row-reverse'), children: [_jsx("span", { children: label }), _jsx(HelpHint, { label: typeof label === 'string' ? `Что значит «${label}»` : 'Пояснение к колонке', children: hint })] })) : (label) }));
}
/**
 * Заголовки колонок для скелетона загрузки (`LoadingTableCard headerCells`):
 * те же видимые колонки и закрепления, что у загруженной таблицы, — после
 * загрузки шапка не прыгает.
 */
export function loadingHeaderCells(columns, align = () => 'left') {
    return columns.map((c) => (_jsx(Table.HeaderCell, { column: c, className: cn(align(c.id) === 'right' && 'text-right'), children: c.id === ACTIONS_COLUMN ? _jsx("span", { className: "sr-only", children: c.def.label }) : c.def.label }, c.id)));
}
/**
 * Полоса над таблицей: слева — свои элементы (фильтры, переключатели),
 * справа — меню «Колонки».
 */
export function TableColumnsBar({ columns, sort, children, className, }) {
    return (_jsxs("div", { className: cn('flex flex-wrap items-center gap-2', className), children: [children, _jsx(TableColumnsMenu, { columns: columns, sort: sort, allowPinRight: true, className: "ml-auto" })] }));
}
//# sourceMappingURL=table-kit.js.map